import React from 'react';
import { CircleElement } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Input } from '@/ui-components/ui/input';
import { Button } from '@/ui-components/ui/button';
import { Circle, RotateCcw } from 'lucide-react';
import { CommonProperties } from './common';
import { AdvancedStaticDynamicSelector, elementAttributeRegistry } from '../../registry';
import { FillSelector } from './FillSelector';

interface CirclePropertiesProps {
  element: CircleElement;
  onOpenSlotPanel?: () => void;
}

/**
 * Circle Properties Component
 * Displays circle-specific properties like fill and stroke
 */
export const CircleProperties: React.FC<CirclePropertiesProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);

  // Get attribute mappings for circle elements
  const circleMapping = elementAttributeRegistry.get('circle');

  const strokeColorAttribute = circleMapping?.attributes.find(attr => attr.attributeKey === 'stroke.color');

  const handlePropertyChange = (property: keyof CircleElement, value: any) => {
    updateElement(element.id, { [property]: value });
  };

  // Removed unused handleFillColorChange


  const handleStrokeColorChange = (colorValue: any) => {
    const currentStroke = element.stroke || { 
      color: strokeColorAttribute?.defaultValue || { mode: 'static', mediaType: 'color', staticValue: '#000000' }, 
      width: 1 
    };
    handlePropertyChange('stroke', { ...currentStroke, color: colorValue });
  };

  const handleStrokeWidthChange = (width: number) => {
    const currentStroke = element.stroke || { 
      color: strokeColorAttribute?.defaultValue || { mode: 'static', mediaType: 'color', staticValue: '#000000' }, 
      width: 1 
    };
    handlePropertyChange('stroke', { ...currentStroke, width });
  };

  const makeCircle = () => {
    const size = Math.min(element.width, element.height);
    handlePropertyChange('width', size);
    handlePropertyChange('height', size);
  };

  // Helper function to get current stroke color as StaticOrDynamic
  const getCurrentStrokeColor = () => {
    if (element.stroke?.color) {
      return element.stroke.color; // element.stroke.color ist bereits ein ColorValue
    }
    return strokeColorAttribute?.defaultValue || {
      mode: 'static' as const,
      mediaType: 'color' as const,
      staticValue: '#000000'
    };
  };

  return (
    <div className="space-y-4">
      <CommonProperties element={element} />
      
      {/* Circle-specific Properties */}
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Circle className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Kreis</Label>
        </div>
        
        {/* Make Perfect Circle */}
        <Button
          variant="outline"
          size="sm"
          onClick={makeCircle}
          className="w-full h-8 text-[11px] rounded-lg border-gray-200 gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Perfekten Kreis erstellen
        </Button>
        
        {/* Fill Selector */}
        <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
          <FillSelector 
            value={element.fill} 
            onChange={(fill) => handlePropertyChange('fill', fill)}
            onOpenSlotPanel={onOpenSlotPanel || (() => {})}
          />
        </div>
        
        {/* Stroke Properties */}
        {element.stroke && (
          <div className="space-y-4">
            {/* Stroke Color - Using AdvancedStaticDynamicSelector */}
            {strokeColorAttribute && (
              <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
                <AdvancedStaticDynamicSelector
                  label="Rahmenfarbe"
                  value={getCurrentStrokeColor()}
                  onChange={handleStrokeColorChange}
                  compatibleMediaTypes={strokeColorAttribute.compatibleMediaTypes}
                  required={strokeColorAttribute.required}
                  onOpenSlotPanel={onOpenSlotPanel}
                />
              </div>
            )}
            
            {/* Stroke Width - Static Input */}
            <div className="space-y-1.5">
              <Label className="text-[11px] text-gray-500 font-medium">Rahmenbreite</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={element.stroke.width}
                  onChange={(e) => handleStrokeWidthChange(parseFloat(e.target.value) || 0)}
                  className="h-8 text-[11px] rounded-lg border-gray-200 pr-7"
                  min="0"
                  step="0.5"
                />
                <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">px</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};